import { FC, ReactNode, RefObject, useEffect, useRef } from 'react';
import { useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';

import { ButtonPrimary } from '@legacy/components/ui-components/Button';
import styles from './AppShell.module.scss';
import { BrowserTitle } from './components';
import cx from 'classnames';
import { useHasNewNavigation } from '@/hooks/featureFlags';

interface AppShellProps {
  children: ReactNode;
  /** Title for the tab bar in browser */
  title?: string;
  /** ref to content element - usually a <main> tag */
  mainRefElement: RefObject<HTMLElement | null>;
}

// This component is used for wrapping each layout we have
// to ensure that correct things a applied in <head>
// and that we have a "skip to content" button.
export const AppShell: FC<AppShellProps> = ({
  children,
  title,
  mainRefElement,
}) => {
  const { t } = useTranslation('header');
  const ref = useRef<HTMLSpanElement>(null);
  const { pathname } = useLocation();

  const hasNewNavigation = useHasNewNavigation();

  // remove once react-router accessibility issue is fixed
  // https://github.com/ReactTraining/react-router/issues/5210
  useEffect(() => {
    ref.current?.focus();
  }, [pathname]);

  const skipToMainContent = () => {
    if (mainRefElement) {
      setTimeout(() => {
        if (mainRefElement.current) {
          mainRefElement.current.setAttribute('tabIndex', '-1');
          mainRefElement.current.focus();
        }
      }, 100);
    }
  };

  return (
    <>
      <BrowserTitle title={title} />
      <div
        className={cx(styles.App, {
          [styles.AppHasNewNavigation]: hasNewNavigation,
        })}
      >
        {!hasNewNavigation ? (
          <>
            <span
              ref={ref}
              tabIndex={-1}
              data-automation-id="AppShellInitialFocusPoint"
            />
            <ButtonPrimary
              onClick={skipToMainContent}
              data-skip-link
              data-automation-id="AppShellSkipToContentButton"
            >
              {t('SkipToContentButton')}
            </ButtonPrimary>
          </>
        ) : null}

        {children}
      </div>
    </>
  );
};
